// screen: Catch-up — direct entry from the main menu (one channel: straight to its programme list)
// Dmitry 2026-09-30 "add direct access to catch-ups from the main menu". With several channels: each channel with its
// latest live still and its most recent programmes (newest first), every one linking into that channel's catch-up list.
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Film, History } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { EmptyState, ErrorState, Skeleton } from '@viewstream/ui';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { useChannels, useNow, type Channel } from '~/app/live';
import { LiveTilePreview } from '~/app/LiveTilePreview';
import { LIVE_STILL_REFRESH_S } from '~/app/thumbs';
import { useCatchupProgrammes } from '~/app/programmeVod';

export const Route = createFileRoute('/_layout/t/$slug/catchup')({ component: Screen });

function Screen() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  React.useEffect(() => { document.title = appTitle(t`Catch-up`); }, [t]);
  const q = useChannels(tenant.slug);
  const now = useNow(LIVE_STILL_REFRESH_S * 1000);
  const list = q.data ?? [];
  // one channel: its catch-up programme list directly (replace, so Back does not bounce here)
  React.useEffect(() => {
    if (q.data && q.data.length === 1) void navigate({ to: '/t/$slug/live/$channelId' as never, params: { slug: tenant.slug, channelId: q.data[0]!.id } as never, search: { tab: 'catchup', view: 'list' } as never, replace: true });
  }, [q.data, navigate, tenant.slug]);

  if (q.isPending || list.length === 1) return <div className="space-y-3"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load channels`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  return (
    <div>
      <h1 className="mb-4 text-2xl font-semibold">{t`Catch-up`}</h1>
      {list.length === 0 ? (
        <EmptyState icon={<History className="size-8" aria-hidden />} title={t`No channels yet`} description={t`Catch-up appears here once a channel records.`} />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-label={t`Channels`} data-testid="catchup-landing">
          {list.map((c) => <ChannelCatchup key={c.id} c={c} now={now} />)}
        </ul>
      )}
    </div>
  );
}

function ChannelCatchup({ c, now }: { c: Channel; now: number }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const q = useCatchupProgrammes(tenant.slug, c.id!, now - 24 * 3_600_000);
  const recent = (q.data ?? []).filter((p) => p.coverage > 0 && !p.airing).sort((a, b) => Date.parse(b.start_at) - Date.parse(a.start_at)).slice(0, 5);
  const name = c.title ?? c.slug ?? '';
  const toChannel = { to: '/t/$slug/live/$channelId' as never, params: { slug: tenant.slug, channelId: c.id } as never };
  return (
    <li className="overflow-hidden rounded-lg border border-border bg-surface shadow-card" data-channel={c.slug}>
      <Link {...toChannel} search={{ tab: 'catchup', view: 'list' } as never} className="block focus-visible:outline-2 focus-visible:outline-focus">
        <LiveTilePreview channel={c} cdn={tenant.cdn_hostname} tenant={tenant.slug} now={now} />
        <span className="block p-3 font-medium">{name}</span>
      </Link>
      <div className="border-t border-border p-3">
        <h2 className="mb-2 text-sm font-semibold">{t`Recent programmes`}</h2>
        {q.isPending ? <Skeleton className="h-20" /> : recent.length === 0 ? <p className="text-sm text-fg-muted">{t`No recorded programmes in the last 24 hours.`}</p> : (
          <ul className="space-y-1.5">
            {recent.map((p) => (
              <li key={p.id}>
                <Link {...toChannel} search={{ tab: 'catchup', view: 'list', focus: p.id } as never} className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-focus">
                  {p.thumbnail ? <img src={p.thumbnail} alt="" loading="lazy" className="aspect-video w-16 shrink-0 rounded object-cover" /> : <Film className="size-4 shrink-0 text-fg-muted" aria-hidden />}
                  <span className="min-w-0 flex-1 truncate"><bdi>{p.title ?? t`Programme`}</bdi></span>
                  <span className="num shrink-0 text-xs text-fg-muted">{fmtDateTime(p.start_at, { locale, tz }, { dateStyle: undefined, timeStyle: 'short' })}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
